import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { toProperCase } from '../../lib/utils';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { customersAPI, partsAPI } from '../../services/api';
import { Search, Loader2, ShoppingCart, Plus, X } from 'lucide-react';

export function DirectOrderModal({
  open,
  onOpenChange,
  orderForm,
  setOrderForm,
  orderItems,
  setOrderItems,
  selectedCustomer,
  setSelectedCustomer,
  onCreateOrder,
  creatingOrder,
  formatCurrency,
  calculateOrderTotal,
  calculateTaxAmount,
  calculateGrandTotal,
  resetForm,
}) {
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [partSearch, setPartSearch] = useState('');
  const [partResults, setPartResults] = useState([]);

  useEffect(() => {
    if (customerSearch.length >= 2) {
      customersAPI.list({ search: customerSearch, limit: 10 }).then(res => {
        setCustomers(res.data?.items || res.data || []);
      });
    } else {
      setCustomers([]);
    }
  }, [customerSearch]);

  useEffect(() => {
    if (partSearch.length >= 2) {
      partsAPI.search(partSearch).then(res => {
        setPartResults(res.data?.items || res.data?.data || []);
      });
    } else {
      setPartResults([]);
    }
  }, [partSearch]);

  const addPartToOrder = (part) => {
    if (orderItems.find(item => item.part_id === part.id)) {
      toast.error('Part already added');
      return;
    }
    setOrderItems([...orderItems, {
      id: Date.now().toString(),
      part_id: part.id,
      part_internal_no: part.internal_part_no,
      description: part.description,
      quantity: 1000,
      unit_price: 0,
      uom: 'pcs',
      weight_grams: part.net_weight_grams || 0,
      tooling_cost: 0,
      sample_cost: 0,
      production_lead_time: '',
      sample_lead_time: '',
      is_manual: false,
    }]);
    setPartSearch('');
    setPartResults([]);
  };

  const addManualOrderItem = () => {
    setOrderItems([...orderItems, {
      id: Date.now().toString(),
      part_id: null,
      part_internal_no: '',
      description: '',
      quantity: 1000,
      unit_price: 0,
      uom: 'pcs',
      weight_grams: 0,
      tooling_cost: 0,
      sample_cost: 0,
      production_lead_time: '',
      sample_lead_time: '',
      is_manual: true,
    }]);
  };

  const updateOrderItem = (id, field, value) => {
    setOrderItems(orderItems.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const removeOrderItem = (id) => {
    setOrderItems(orderItems.filter(item => item.id !== id));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Create Direct Order
          </DialogTitle>
          <DialogDescription>
            Create a sales order without a quotation (for legacy orders)
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Customer Selection */}
          <div className="space-y-2">
            <Label>Customer *</Label>
            {!selectedCustomer ? (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search customers..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  className="pl-9"
                />
                {customers.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-background border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {customers.map((c) => (
                      <div
                        key={c.id}
                        className="p-3 hover:bg-muted/50 cursor-pointer border-b last:border-0"
                        onClick={() => {
                          setSelectedCustomer(c);
                          setOrderForm(prev => ({ ...prev, currency: c.currency || 'INR' }));
                          setCustomerSearch('');
                          setCustomers([]);
                        }}
                      >
                        <div className="flex justify-between">
                          <span className="font-medium">{c.name}</span>
                          <Badge variant="outline">{c.customer_code}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-3 bg-primary/5 rounded-lg border flex justify-between items-center">
                <div>
                  <p className="font-bold">{selectedCustomer.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedCustomer.customer_code}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedCustomer(null)}>Change</Button>
              </div>
            )}
          </div>

          {/* Order Details */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Customer PO Number</Label>
              <Input
                value={orderForm.customer_po_number}
                onChange={(e) => setOrderForm(prev => ({ ...prev, customer_po_number: e.target.value }))}
                placeholder="PO-2026-001"
              />
            </div>
            <div className="space-y-2">
              <Label>Customer PO Date</Label>
              <Input
                type="date"
                value={orderForm.customer_po_date}
                onChange={(e) => setOrderForm(prev => ({ ...prev, customer_po_date: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select value={orderForm.currency} onValueChange={(v) => setOrderForm(prev => ({ ...prev, currency: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="INR">INR (&#8377;)</SelectItem>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="EUR">EUR (&euro;)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Payment Terms</Label>
              <Input
                value={orderForm.payment_terms}
                onChange={(e) => setOrderForm(prev => ({ ...prev, payment_terms: e.target.value }))}
                onBlur={(e) => e.target.value && setOrderForm(prev => ({ ...prev, payment_terms: toProperCase(e.target.value) }))}
                placeholder="Net 30"
              />
            </div>
          </div>

          {/* Tax & Delivery */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Tax / GST (%)</Label>
              <Input
                type="number"
                step="0.01"
                value={orderForm.tax_percentage}
                onChange={(e) => setOrderForm(prev => ({ ...prev, tax_percentage: parseFloat(e.target.value) || 0 }))}
                placeholder="18"
                data-testid="direct-order-tax-percentage"
              />
            </div>
            <div className="space-y-2">
              <Label>Delivery Terms</Label>
              <Input
                value={orderForm.delivery_terms || ''}
                onChange={(e) => setOrderForm(prev => ({ ...prev, delivery_terms: e.target.value }))}
                onBlur={(e) => e.target.value && setOrderForm(prev => ({ ...prev, delivery_terms: toProperCase(e.target.value) }))}
                placeholder="Ex-Works"
              />
            </div>
          </div>

          {/* Line Items */}
          <div className="space-y-2">
            <Label>Line Items *</Label>
            
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search parts to add..."
                  value={partSearch}
                  onChange={(e) => setPartSearch(e.target.value)}
                  className="pl-9"
                />
                {partResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-background border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {partResults.map((p) => (
                      <div
                        key={p.id}
                        className="p-3 hover:bg-muted/50 cursor-pointer border-b last:border-0"
                        onClick={() => addPartToOrder(p)}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="font-mono font-medium">{p.internal_part_no}</span>
                            <span className="text-sm text-muted-foreground ml-2">{p.description}</span>
                          </div>
                          <Plus className="h-4 w-4 text-primary" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button 
                type="button" 
                variant="outline" 
                onClick={addManualOrderItem}
                className="whitespace-nowrap"
                data-testid="add-manual-item-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Manual Entry
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Search parts from Part Master or click &quot;Manual Entry&quot; to add legacy items without master data
            </p>

            {orderItems.length > 0 && (
              <div className="border rounded-lg overflow-x-auto">
                <div style={{ minWidth: '1200px' }}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead style={{ minWidth: '140px' }}>Part No</TableHead>
                        <TableHead style={{ minWidth: '200px' }}>Description</TableHead>
                        <TableHead style={{ minWidth: '100px' }}>Qty</TableHead>
                        <TableHead style={{ minWidth: '120px' }}>Unit Price</TableHead>
                        <TableHead style={{ minWidth: '90px' }}>UOM</TableHead>
                        <TableHead style={{ minWidth: '100px' }}>Weight (g)</TableHead>
                        <TableHead style={{ minWidth: '100px' }}>Tooling</TableHead>
                        <TableHead style={{ minWidth: '100px' }}>Sample</TableHead>
                        <TableHead style={{ minWidth: '100px' }}>Sample LT</TableHead>
                        <TableHead style={{ minWidth: '100px' }}>Mfg LT</TableHead>
                        <TableHead style={{ minWidth: '120px' }} className="text-right">Line Total</TableHead>
                        <TableHead style={{ minWidth: '50px' }}></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orderItems.map((item) => {
                        const lineTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
                        return (
                          <TableRow key={item.id} className={item.is_manual ? 'bg-amber-50' : ''}>
                            <TableCell>
                              <Input
                                value={item.part_internal_no}
                                onChange={(e) => updateOrderItem(item.id, 'part_internal_no', e.target.value)}
                                className="h-8 text-sm font-mono"
                                style={{ minWidth: '120px' }}
                                placeholder={item.is_manual ? 'Manual entry' : ''}
                                data-testid={`item-part-no-${item.id}`}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={item.description}
                                onChange={(e) => updateOrderItem(item.id, 'description', e.target.value)}
                                onBlur={(e) => e.target.value && updateOrderItem(item.id, 'description', toProperCase(e.target.value))}
                                className="h-8 text-sm"
                                style={{ minWidth: '180px' }}
                                placeholder="Item description"
                                data-testid={`item-description-${item.id}`}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateOrderItem(item.id, 'quantity', e.target.value)}
                                className="h-8 text-sm"
                                style={{ minWidth: '80px' }}
                                data-testid={`item-qty-${item.id}`}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.00001"
                                value={item.unit_price}
                                onChange={(e) => updateOrderItem(item.id, 'unit_price', e.target.value)}
                                className="h-8 text-sm"
                                style={{ minWidth: '100px' }}
                                data-testid={`item-price-${item.id}`}
                              />
                            </TableCell>
                            <TableCell>
                              <Select value={item.uom} onValueChange={(v) => updateOrderItem(item.id, 'uom', v)}>
                                <SelectTrigger className="h-8" style={{ minWidth: '70px' }}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pcs">pcs</SelectItem>
                                  <SelectItem value="kgs">kgs</SelectItem>
                                  <SelectItem value="sets">sets</SelectItem>
                                  <SelectItem value="lbs">lbs</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={item.weight_grams || ''}
                                onChange={(e) => updateOrderItem(item.id, 'weight_grams', e.target.value)}
                                className="h-8 text-sm"
                                style={{ minWidth: '80px' }}
                                placeholder="0"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={item.tooling_cost}
                                onChange={(e) => updateOrderItem(item.id, 'tooling_cost', e.target.value)}
                                className="h-8 text-sm"
                                style={{ minWidth: '80px' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={item.sample_cost}
                                onChange={(e) => updateOrderItem(item.id, 'sample_cost', e.target.value)}
                                className="h-8 text-sm"
                                style={{ minWidth: '80px' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                placeholder="Weeks"
                                value={item.sample_lead_time}
                                onChange={(e) => updateOrderItem(item.id, 'sample_lead_time', e.target.value)}
                                className="h-8 text-sm"
                                style={{ minWidth: '80px' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                placeholder="Weeks"
                                value={item.production_lead_time}
                                onChange={(e) => updateOrderItem(item.id, 'production_lead_time', e.target.value)}
                                className="h-8 text-sm"
                                style={{ minWidth: '80px' }}
                              />
                            </TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(lineTotal, orderForm.currency)}</TableCell>
                            <TableCell>
                              <Button variant="ghost" size="icon" onClick={() => removeOrderItem(item.id)}>
                                <X className="h-4 w-4 text-red-500" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {orderItems.length > 0 && (
              <div className="pt-2 border-t">
                <div className="flex justify-end">
                  <div className="w-72 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal:</span>
                      <span className="font-mono">{formatCurrency(calculateOrderTotal(), orderForm.currency)}</span>
                    </div>
                    {(parseFloat(orderForm.tax_percentage) || 0) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Tax ({orderForm.tax_percentage}%):</span>
                        <span className="font-mono">{formatCurrency(calculateTaxAmount(), orderForm.currency)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                      <span>Grand Total:</span>
                      <span className="font-mono text-primary">{formatCurrency(calculateGrandTotal(), orderForm.currency)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Ship To Address</Label>
            <Textarea
              value={orderForm.ship_to_address || ''}
              onChange={(e) => setOrderForm(prev => ({ ...prev, ship_to_address: e.target.value }))}
              placeholder="Delivery / shipping address (leave blank to use customer billing address)"
              rows={3}
              data-testid="direct-order-ship-to-address"
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={orderForm.notes}
              onChange={(e) => setOrderForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Internal notes..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); resetForm(); }}>
            Cancel
          </Button>
          <Button 
            className="btn-primary" 
            onClick={onCreateOrder}
            disabled={creatingOrder || !selectedCustomer || orderItems.length === 0}
          >
            {creatingOrder ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</>
            ) : (
              <><ShoppingCart className="mr-2 h-4 w-4" />Create Order</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
